import React, { useCallback, useEffect, useReducer, useRef } from "react";
import toast from "react-hot-toast";

import EmailLookupStep from "./EmailLookupStep";
import ExistingPatientLoginStep from "./ExistingPatientLoginStep";
import MissingProfileFieldsStep from "./MissingProfileFieldsStep";
import NewPatientRegistrationStep from "./NewPatientRegistrationStep";
import PasswordResetStep from "./PasswordResetStep";
import {
  AUTH_ACCOUNT_STATUS,
  AUTH_BIND_DISPOSITION,
  AUTH_ERROR,
  AUTH_EVENT,
  AUTH_STATE,
} from "./authenticationConstants";
import {
  authenticationReducer,
  initialAuthenticationContext,
  requiresProfileCompletion,
} from "./authenticationMachine";
import {
  clearLookupCache,
  completePatientProfile,
  createPatientAccount,
  loginPatient,
  lookupEmail,
} from "./authenticationApi";
import {
  buildProfileCompletionPayload,
  buildRegistrationPayload,
} from "./authenticationPayload";

const initializeAuthenticationContext = ({ authentication, email }) =>
  authentication?.accessToken && authentication?.patient
    ? {
        ...initialAuthenticationContext,
        state: AUTH_STATE.authenticated,
        email: authentication.patient.email || "",
        patient: authentication.patient,
        accessToken: authentication.accessToken,
        profile: authentication.profile || null,
        missingFields: authentication.profile?.missing_fields || [],
      }
    : email
      ? {
          ...initialAuthenticationContext,
          state: AUTH_STATE.existingCredentials,
          email,
        }
      : initialAuthenticationContext;

const normalizeBindingOutcome = (result, fallbackProfile) => {
  if (
    result
    && Object.values(AUTH_BIND_DISPOSITION).includes(result.disposition)
  ) {
    return {
      disposition: result.disposition,
      profile: result.profile || fallbackProfile || null,
    };
  }
  const profile = result || fallbackProfile || null;
  return {
    disposition: profile?.missing_fields?.length
      ? AUTH_BIND_DISPOSITION.profileIncomplete
      : AUTH_BIND_DISPOSITION.ready,
    profile,
  };
};

/** Patient Authentication boundary for Program intakes. */
export function TreatmentAuthenticationStep({
  onAuthenticated,
  onIdentityReset,
  onReady,
  brandName = "WellieMD",
  previewMode = false,
  previewExistingPatient = false,
  previewPatientProfile = null,
  apiBaseUrl = null,
  initialAuthentication = null,
  initialEmail = "",
}) {
  const [context, dispatch] = useReducer(
    authenticationReducer,
    { authentication: initialAuthentication, email: initialEmail },
    initializeAuthenticationContext,
  );
  const registrationKey = useRef(null);
  const loginInFlightRef = useRef(false);
  const shouldRestoreInitialAuthentication = useRef(Boolean(
    initialAuthentication?.accessToken && initialAuthentication?.patient
  ));
  const restoredAuthenticationHandled = useRef(false);
  const initialAuthenticationRef = useRef(initialAuthentication);
  const restoredAuthenticationMountedRef = useRef(false);
  const onAuthenticatedRef = useRef(onAuthenticated);
  const onReadyRef = useRef(onReady);

  // The restored-authentication operation can outlive a parent render. Keep
  // its completion callbacks current without making the one-time restore
  // effect restart (and cancel) when runtime state changes during binding.
  useEffect(() => {
    onAuthenticatedRef.current = onAuthenticated;
    onReadyRef.current = onReady;
  }, [onAuthenticated, onReady]);

  useEffect(() => {
    restoredAuthenticationMountedRef.current = true;

    return () => {
      restoredAuthenticationMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const restoredAuthentication = initialAuthenticationRef.current;

    if (
      !shouldRestoreInitialAuthentication.current
      || !restoredAuthentication?.accessToken
      || !restoredAuthentication?.patient
      || restoredAuthenticationHandled.current
    ) {
      return;
    }
    restoredAuthenticationHandled.current = true;
    Promise.resolve(onAuthenticatedRef.current?.({
      patient: restoredAuthentication.patient,
      accessToken: restoredAuthentication.accessToken,
      accessTokenExpiresIn: restoredAuthentication.accessTokenExpiresIn,
      profile: restoredAuthentication.profile,
      restored: true,
    }))
      .then((bindingResult) => {
        if (!restoredAuthenticationMountedRef.current) return;
        const outcome = normalizeBindingOutcome(
          bindingResult,
          restoredAuthentication.profile,
        );
        const { profile } = outcome;
        if (outcome.disposition === AUTH_BIND_DISPOSITION.disqualified) {
          dispatch({ type: AUTH_EVENT.bindDisqualified, profile });
          return;
        }
        dispatch({ type: AUTH_EVENT.bindSucceeded, profile });
        if (outcome.disposition === AUTH_BIND_DISPOSITION.ready) {
          onReadyRef.current?.();
        }
      })
      .catch((error) => {
        if (!restoredAuthenticationMountedRef.current) return;
        dispatch({
          type: AUTH_EVENT.sessionExpired,
          error: error?.code,
        });
      });
  }, []);

  const runLookup = useCallback(async (email) => {
    if (previewMode) {
      dispatch({
        type: previewExistingPatient
          ? AUTH_EVENT.lookupFound
          : AUTH_EVENT.lookupNotFound,
      });
      return;
    }
    try {
      const { exists, accountStatus } = await lookupEmail(email, { apiBaseUrl });
      if (accountStatus === AUTH_ACCOUNT_STATUS.inactive) {
        dispatch({ type: AUTH_EVENT.lookupInactive });
        return;
      }
      if (accountStatus === AUTH_ACCOUNT_STATUS.nonPatient) {
        dispatch({ type: AUTH_EVENT.lookupNonPatient });
        return;
      }
      dispatch({
        type: exists ? AUTH_EVENT.lookupFound : AUTH_EVENT.lookupNotFound,
      });
    } catch (error) {
      dispatch({ type: AUTH_EVENT.lookupUnavailable });
    }
  }, [apiBaseUrl, previewExistingPatient, previewMode]);

  const handleSubmitEmail = useCallback(
    (email) => {
      dispatch({ type: AUTH_EVENT.submitEmail, email });
      runLookup(email);
    },
    [runLookup]
  );

  const handleRetryLookup = useCallback(() => {
    dispatch({ type: AUTH_EVENT.retry });
    runLookup(context.email);
  }, [context.email, runLookup]);

  const handleChangeEmail = useCallback(() => {
    clearLookupCache();
    registrationKey.current = null;
    dispatch({ type: AUTH_EVENT.changeEmail });
    onIdentityReset?.();
  }, [onIdentityReset]);

  const handleLogin = useCallback(
    async (credentials) => {
      // `pending` disables the form on the next render, but multiple submit
      // events can arrive before React commits that render. Guard the request
      // synchronously so a rapid double click cannot start another login.
      if (loginInFlightRef.current) return;
      loginInFlightRef.current = true;
      dispatch({ type: AUTH_EVENT.submitCredentials });
      try {
        const result = previewMode
          ? {
              patient_id: "preview-existing-patient",
              access_token: null,
              profile: { missing_fields: [] },
            }
          : await loginPatient(credentials, { apiBaseUrl });
        let patientData = {
          ...(previewMode ? previewPatientProfile : {}),
          patient_id: result.patient_id,
          email: credentials.email,
          ...(result.patient || {}),
        };
        const bindingResult = await onAuthenticated?.({
          patient: patientData,
          accessToken: result.access_token,
          accessTokenExpiresIn: result.access_token_expires_in,
          profile: result.profile,
        });
        const outcome = normalizeBindingOutcome(bindingResult, result.profile);
        const { profile } = outcome;
        dispatch({
          type: AUTH_EVENT.credentialsAccepted,
          patient: patientData,
          accessToken: result.access_token,
          profile,
        });
        if (outcome.disposition === AUTH_BIND_DISPOSITION.disqualified) {
          dispatch({ type: AUTH_EVENT.bindDisqualified, profile });
          return;
        }
        dispatch({ type: AUTH_EVENT.bindSucceeded, profile });
        if (outcome.disposition === AUTH_BIND_DISPOSITION.ready) onReady?.();
      } catch (error) {
        if (error.status === 429) {
          const message = error.userMessage
            || "Too many login attempts. Please wait before trying again.";
          toast.error(message, {
            duration: 7000,
            style: {
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
            },
            iconTheme: {
              primary: "#dc2626",
              secondary: "#fff",
            },
          });
        }
        dispatch({
          type: AUTH_EVENT.credentialsRejected,
          error: error.code,
          errorMessage: error.userMessage,
        });
      } finally {
        // The login request has a 12 second transport timeout. Always release
        // the guard after success or failure so patients can retry.
        loginInFlightRef.current = false;
      }
    },
    [
      apiBaseUrl,
      onAuthenticated,
      onReady,
      previewMode,
      previewPatientProfile,
    ]
  );

  const handleForgotPassword = useCallback(() => {
    dispatch({ type: AUTH_EVENT.forgotPassword });
  }, []);

  const handleBackToLogin = useCallback(() => {
    dispatch({ type: AUTH_EVENT.backToLogin });
  }, []);

  // A reset ends in a normal login, so the runtime binds exactly as it does
  // when the patient types the password on the sign-in step.
  const handlePasswordReset = useCallback((credentials) => {
    dispatch({ type: AUTH_EVENT.backToLogin });
    handleLogin(credentials);
  }, [handleLogin]);

  const handleRegister = useCallback(
    async (form) => {
      dispatch({ type: AUTH_EVENT.submitRegistration });
      if (!registrationKey.current) {
        registrationKey.current =
          globalThis.crypto?.randomUUID?.() || String(Date.now());
      }
      let accountCreated = false;
      try {
        const registrationPayload = buildRegistrationPayload(form);
        const result = previewMode
          ? {
              patient_id: "preview-new-patient",
              access_token: null,
              profile: { missing_fields: [] },
            }
          : await createPatientAccount(
              registrationPayload,
              {
                requestKey: registrationKey.current,
                apiBaseUrl,
              }
            );
        accountCreated = true;
        const patient = {
          ...(previewMode
            ? {
                ...previewPatientProfile,
                date_of_birth: registrationPayload.date_of_birth,
                sex: registrationPayload.sex,
                phone: registrationPayload.phone,
                first_name: registrationPayload.first_name,
                last_name: registrationPayload.last_name,
              }
            : {}),
          patient_id: result.patient_id,
          email: form.email,
        };
        const bindingResult = await onAuthenticated?.({
          patient,
          accessToken: result.access_token,
          accessTokenExpiresIn: result.access_token_expires_in,
          profile: result.profile,
        });
        const outcome = normalizeBindingOutcome(bindingResult, result.profile);
        const { profile } = outcome;
        dispatch({
          type: AUTH_EVENT.accountCreated,
          patient,
          accessToken: result.access_token,
          profile,
        });
        if (outcome.disposition === AUTH_BIND_DISPOSITION.disqualified) {
          dispatch({ type: AUTH_EVENT.bindDisqualified, profile });
          return;
        }
        dispatch({ type: AUTH_EVENT.bindSucceeded, profile });
        if (outcome.disposition === AUTH_BIND_DISPOSITION.ready) onReady?.();
      } catch (error) {
        if (accountCreated) {
          registrationKey.current = null;
          dispatch({
            type: AUTH_EVENT.identityExists,
            error: AUTH_ERROR.authenticationUnavailable,
          });
          return;
        }
        // The API intentionally uses one generic identity-conflict response
        // for email and phone collisions. Keep the patient on registration so
        // a phone-only collision does not send them to login with an email
        // that the lookup just established is new.
        if (error.code === AUTH_ERROR.identityConflict) {
          registrationKey.current = null;
          dispatch({
            type: AUTH_EVENT.validationFailed,
            error: AUTH_ERROR.identityConflict,
          });
          return;
        }
        dispatch({
          type: AUTH_EVENT.validationFailed,
          error: error.code,
          fieldErrors: error.fieldErrors,
        });
      }
    },
    [
      apiBaseUrl,
      onAuthenticated,
      onReady,
      previewMode,
      previewPatientProfile,
    ]
  );

  const handleCompleteProfile = useCallback(
    async (values) => {
      dispatch({ type: AUTH_EVENT.submitProfile });
      try {
        const result = await completePatientProfile(
          buildProfileCompletionPayload(context.missingFields, values),
          { accessToken: context.accessToken, apiBaseUrl }
        );
        const bindingResult = await onAuthenticated?.({
          patient: context.patient,
          accessToken: context.accessToken,
          profile: result.profile,
          profileUpdated: true,
        });
        const outcome = normalizeBindingOutcome(bindingResult, result.profile);
        const { profile } = outcome;
        if (outcome.disposition === AUTH_BIND_DISPOSITION.disqualified) {
          dispatch({ type: AUTH_EVENT.bindDisqualified, profile });
          return;
        }
        dispatch({ type: AUTH_EVENT.bindSucceeded, profile });
        if (outcome.disposition === AUTH_BIND_DISPOSITION.ready) onReady?.();
      } catch (error) {
        dispatch({
          type: AUTH_EVENT.validationFailed,
          error: error.code,
          fieldErrors: error.fieldErrors,
        });
      }
    },
    [
      apiBaseUrl,
      context.accessToken,
      context.missingFields,
      context.patient,
      onAuthenticated,
      onReady,
    ]
  );

  switch (context.state) {
    case AUTH_STATE.inactiveAccount:
    case AUTH_STATE.existingNonPatient:
      return (
        <div className="treatment-auth" role="alert">
          <p className="treatment-auth__eyebrow">Account access</p>
          <h1 className="treatment-auth__title">Existing account found</h1>
          <p className="treatment-auth__description treatment-auth__description--error">
            {context.error === AUTH_ERROR.inactiveAccount
              ? "This email is already associated with an inactive account. Please contact support or use another email address."
              : "This email is already associated with an existing account. Please contact support or use another email address."}
          </p>
          <button
            type="button"
            className="treatment-auth__submit"
            onClick={handleChangeEmail}
          >
            Use another email
          </button>
        </div>
      );
    case AUTH_STATE.existingCredentials:
      return (
        <ExistingPatientLoginStep
          email={context.email}
          pending={context.pending}
          error={context.error}
          errorMessage={context.errorMessage}
          onSubmit={handleLogin}
          onChangeEmail={handleChangeEmail}
          onForgotPassword={previewMode ? undefined : handleForgotPassword}
        />
      );
    case AUTH_STATE.passwordReset:
      return (
        <PasswordResetStep
          email={context.email}
          apiBaseUrl={apiBaseUrl}
          onComplete={handlePasswordReset}
          onBack={handleBackToLogin}
        />
      );
    case AUTH_STATE.newRegistration:
      return (
        <NewPatientRegistrationStep
          email={context.email}
          pending={context.pending}
          error={context.error}
          fieldErrors={context.fieldErrors}
          brandName={brandName}
          onSubmit={handleRegister}
          onUseExistingAccount={handleChangeEmail}
        />
      );
    case AUTH_STATE.authenticated:
    case AUTH_STATE.intakeReady:
      if (requiresProfileCompletion(context)) {
        return (
          <MissingProfileFieldsStep
            missingFields={context.missingFields}
            pending={context.pending}
            fieldErrors={context.fieldErrors}
            onSubmit={handleCompleteProfile}
          />
        );
      }
      // The journey has taken over; the boundary renders nothing further.
      return null;
    case AUTH_STATE.eligibilityBlocked:
      // The questionnaire shell owns the authoritative eligibility message.
      return null;
    case AUTH_STATE.lookupRetry:
    case AUTH_STATE.lookupPending:
    case AUTH_STATE.unidentified:
    default:
      return (
        <EmailLookupStep
          email={context.email}
          brandName={brandName}
          pending={context.pending}
          unavailable={context.state === AUTH_STATE.lookupRetry}
          onSubmit={handleSubmitEmail}
          onRetry={handleRetryLookup}
        />
      );
  }
}

export default TreatmentAuthenticationStep;
